import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getReport } from '../api/dashboard';
import { errorCode } from '../api/client';
import OffUnitMessage from './OffUnitMessage';
import PhoneFrame from './PhoneFrame';
import PhoneHomeScreen from './PhoneHomeScreen';
import PhoneReportScreen, { PhoneBackButton } from './PhoneReportScreen';
import './PhoneApp.css';

// The phone's open patient lives in the URL (?patient=<reportId>), so the
// browser's back and forward, a refresh, or a shared link show the same
// screen the phone does.
const PARAM = 'patient';

// A Kabilah app simulator: a nurse's home screen inside the phone, and
// tapping a patient opens their report sheet in the same phone, with ‹ going
// back to the home screen, like the app. Nothing on the page around it moves.
const PhoneApp = ({ firstName, nurseName, sheets, label, caption, blankText }) => {
  const [params, setParams] = useSearchParams();
  const openId = params.get(PARAM);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const open = (sheet) => setParams((p) => { p.set(PARAM, sheet.reportId); return p; });
  const home = useCallback(() => setParams((p) => { p.delete(PARAM); return p; }), [setParams]);

  useEffect(() => {
    if (!openId) { setReport(null); setError(null); return undefined; }
    let cancelled = false;
    setReport(null);
    setError(null);
    getReport(openId)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((err) => { if (!cancelled && errorCode(err) !== 'unauthenticated') setError(errorCode(err)); });
    return () => { cancelled = true; };
  }, [openId]);

  const listed = sheets.find((s) => s.reportId === openId);
  let screen;
  if (!openId) {
    screen = <PhoneHomeScreen firstName={firstName} sheets={sheets} onOpen={open} blankText={blankText} />;
  } else if (report) {
    screen = <PhoneReportScreen report={report} onBack={home} backLabel="Back to patients" />;
  } else {
    const offUnit = error === 'sheet_not_on_unit' || (error === 'not_found' && listed);
    screen = (
      <>
        <div className="app-nav"><PhoneBackButton onBack={home} label="Back to patients" /></div>
        {!error && <div className="phone-app-loading" aria-label="Loading report sheet"><div className="spinner" /></div>}
        {offUnit && (
          <OffUnitMessage inPhone patientName={listed?.name} room={listed?.room} nurseName={nurseName} />
        )}
        {error && !offUnit && (
          <p className="phone-app-error" role="alert">
            {error === 'not_found'
              ? "This report sheet isn't available anymore. The patient may have been discharged."
              : error === 'network_error'
                ? "Can't reach the dashboard server. Check your connection."
                : "We couldn't load this report sheet."}
          </p>
        )}
      </>
    );
  }

  return <PhoneFrame label={label} caption={caption}>{screen}</PhoneFrame>;
};

export default PhoneApp;
